import React, { useMemo } from 'react';

import { customsInstructionApis } from '@apis/next/customsInstructionApis';
import { Button } from '@components/Button';
import { Layout } from '@components/Layout';
import { GridForm } from '@components/NextForms/GridForm';
import { PaginationWithSearchQuery } from '@components/PaginationWithSearchQuery';
import { QueryContainer } from '@components/QueryContainer';
import { cellFactory, Table } from '@components/Table/Table';
import { VerticalSpace } from '@components/VerticalSpace';
import {
  getInstructionStatusByClientLabel,
  getInstructionStatusLabel,
  getInstructionStatusMeta,
  instructionStrings,
} from '@data/instruction';
import { useSearch } from '@hooks/next/useSearch';
import {
  CustomsInstructionSearchForm,
  CustomsInstructionSearchQuery,
} from '@models/next/CustomsInstruction';
import { CustomsInstructionHelper } from '@models/next/CustomsInstructionHelper';
import { empty } from '@monoid-dev/mobx-zod-form';
import { useForm } from '@monoid-dev/mobx-zod-form-react';
import { useCommonStyles, makeMuiStyles, useIsMobile } from '@styles';
import { formStrings } from '@utils/forms';
import { createStrings } from '@utils/local';
import { url } from '@utils/pages';
import { DEFAULT_CLIENT_PAGE_SIZE, formatDate, mapOptional, stringifySearch } from '@utils/types';
import clsx from 'clsx';
import { Link } from 'react-router-dom';

const strings = createStrings({
  ja: {
    d2dNo: 'D2D番号',
    all: 'すべて',
    no: 'NO.',
    etd: '出港予定日',
    eta: '入港予定日',
    toBookingDetails: '案件詳細へ',
    prompt:
      '*送信済の通関書類、もしくは海上保険を変更する場合、左下の「...」アイコンをクリックして、D2Dカスタマーセンターへご連絡ください。',
  },
});

const useStyles = makeMuiStyles((theme, common) => {
  return {
    link: {
      color: theme.palette.primary.main,
      whiteSpace: 'pre',
    },
    container: {
      [theme.breakpoints.up('lg')]: {
        padding: '1rem 2rem',
      },
      backgroundColor: theme.palette.background.paper,
      flex: 1,
    },
    inner: {
      ...common.flexColumn,
      maxWidth: '1600px',
      padding: '0rem 1rem',
      margin: '0 auto',
    },
    innerContent: {
      flex: 1,
      padding: '1rem 2rem',
      overflow: 'hidden',
      ...common.verticalSpace3,
    },
    searchContainer: {
      ...common.normalText,
      paddingTop: 23,
    },
    formContainer: {
      ...common.flexColumn,
      ...common.verticalSpace1,
    },
    paginationContainer: {
      ...common.flexColumn,
      flex: 1,
      justifyContent: 'flex-end',
      alignItems: 'flex-end',
    },
    startContainer: {
      ...common.flexInline,
      columnGap: '1rem',
      [theme.breakpoints.down('sm')]: {
        ...common.flexColumn,
        rowGap: '0.5rem',
      },
    },
    badge: {
      height: 8,
      width: 8,
      borderRadius: '50%',
      backgroundColor: theme.palette.warning.dark,
      position: 'absolute',
      top: -5,
      right: -5,
      paddingBottom: 2,
      zIndex: 2,
    },
    errorRow: {
      '& * ': {
        color: theme.palette.error.main,
      },
    },
  };
});

const CustomsInstructionSearch: React.FC = () => {
  const { search, updateSearch } = useSearch(CustomsInstructionSearchQuery);

  const initialOutput = useMemo(
    (): CustomsInstructionSearchForm => ({
      ...search,
      d2d_no: search.d2d_no ?? empty,
      status: search?.status?.length
        ? getInstructionStatusMeta(search.status[0]).clientLabel
        : 'すべて',
    }),
    [search],
  );

  const form = useForm(CustomsInstructionSearchForm, {
    enableReinitialize: true,
    initialOutput,
  });

  const isMobile = useIsMobile();

  const { fields } = form.root;

  return (
    <form
      {...form.bindForm({
        onSubmit(values) {
          updateSearch({
            ...values,
            page: 1,
            status:
              values.status !== 'すべて'
                ? [getInstructionStatusByClientLabel(values.status)]
                : undefined,
          });
        },
      })}
    >
      <GridForm
        columns={isMobile ? 1 : 4}
        fields={[fields.d2d_no, fields.status]}
        buttons={
          <>
            <Button
              color="primary"
              size="small"
              type="submit"
              text={formStrings.search}
              style={{ width: 88 }}
            />
            <Button
              color="primary"
              size="small"
              type="submit"
              text={formStrings.reset}
              onClick={() => form.root.setOutput(empty)}
              style={{ width: 88 }}
            />
          </>
        }
      />
    </form>
  );
};

export const CustomsInstructionList: React.FC = () => {
  const { search } = useSearch(CustomsInstructionSearchQuery);
  const classes = { ...useStyles(), ...useCommonStyles() };
  const customsInstructionList = customsInstructionApis.useGet('/customs-instruction', {
    queries: search,
  });

  const currentPath = window.location.pathname + window.location.search;

  return (
    <Layout
      title={instructionStrings.customsInstruction}
      breadcrumbs={['/utilities/Dashboard', '/utilities/Bookings/CustomsInstructionList']}
    >
      <div className={classes.container}>
        <div className={classes.inner}>
          <div className={clsx(classes.pageTitleText, classes.textPrimary)}>
            {instructionStrings.customsInstruction}
          </div>
          <VerticalSpace height={16} />
          <CustomsInstructionSearch />
          <VerticalSpace height={30} />
          <div className={clsx(classes.textPrimary, classes.middleText, classes.fontWeight700)}>
            {strings.prompt}
          </div>
          <VerticalSpace height={8} />
          <QueryContainer queries={[customsInstructionList]}>
            <Table>
              <Table.Head>
                <Table.Row
                  columns={[
                    cellFactory.create(strings.no),
                    cellFactory.create(strings.d2dNo),
                    cellFactory.create(instructionStrings.deadline),
                    cellFactory.create(instructionStrings.status),
                    cellFactory.create(strings.etd),
                    cellFactory.create(strings.eta),
                    cellFactory.create(''),
                  ]}
                />
              </Table.Head>
              <Table.Body>
                {customsInstructionList.data?.results?.map((item, index) => {
                  const lastStatus = CustomsInstructionHelper.getCurrentVisibleStatus(item);
                  const isOverdue = CustomsInstructionHelper.isOverdue(item);

                  return (
                    <Table.Row
                      className={isOverdue ? classes.errorRow : undefined}
                      key={index}
                      columns={[
                        cellFactory.create(index + 1),
                        cellFactory.create(
                          <Link
                            to={`${url('/utilities/Bookings/CustomsInstruction')}?${stringifySearch(
                              { from: currentPath, d2dNo: item.d2d_no },
                            )}`}
                            className={clsx(classes.link, classes.underline)}
                            target="_blank"
                          >
                            {item.d2d_no}
                          </Link>,
                        ),
                        cellFactory.create(formatDate(item.cutoff_date)),
                        cellFactory.create(
                          getInstructionStatusLabel(lastStatus?.name ?? '未送信', false),
                        ),
                        cellFactory.create(mapOptional(item.etd, formatDate) ?? '-'),
                        cellFactory.create(mapOptional(item.eta, formatDate) ?? '-'),
                        cellFactory.create(
                          <Link
                            to={`${url('/utilities/Bookings/CurrentBookingDetails')}?d2dNo=${
                              item.d2d_no
                            }`}
                            className={classes.link}
                          >
                            <span style={{ position: 'relative' }}>
                              {['未送信', '修正依頼済'].includes(
                                item.status[item.status.length - 1].name,
                              ) && <div className={classes.badge} />}
                              {strings.toBookingDetails}
                            </span>
                          </Link>,
                        ),
                      ]}
                    />
                  );
                })}
              </Table.Body>
            </Table>
            <br />
            <div className={clsx(classes.flexInline, classes.justifyEnd)}>
              <PaginationWithSearchQuery
                query={customsInstructionList}
                pageSize={DEFAULT_CLIENT_PAGE_SIZE}
              />
            </div>
          </QueryContainer>
        </div>
      </div>
    </Layout>
  );
};
